import React, { useState, useEffect } from 'react';
import { Upload, Download, AlertCircle, CheckCircle, BarChart3 } from 'lucide-react';
import apiClient from '../services/api';
import DashboardLayout from './DashboardLayout';

const CargaMasivaPreguntasContent = () => {
    const [archivo, setArchivo] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [resumenPreguntas, setResumenPreguntas] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        cargarResumenPreguntas();
    }, []);

    const cargarResumenPreguntas = async () => {
        try {
            const response = await apiClient.get('/bulk/resumen-preguntas');
            setResumenPreguntas(response.data);
        } catch (error) {
            setError('Error cargando resumen: ' + error.message);
        }
    };

    const descargarPlantilla = async () => {
        try {
            setCargando(true);
            setError('');
            
            const response = await apiClient.get('/bulk/plantilla-preguntas', {
                responseType: 'blob'
            });
            
            // Crear URL para descarga
            const blob = new Blob([response.data], { 
                type: 'text/csv; charset=utf-8' 
            });
            const url = window.URL.createObjectURL(blob);
            
            // Crear elemento de descarga
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `plantilla_preguntas_${new Date().toISOString().split('T')[0]}.csv`;
            
            // Ejecutar descarga
            document.body.appendChild(a);
            a.click();
            
            // Limpiar
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (error) {
            setError('Error descargando plantilla: ' + error.message);
        } finally {
            setCargando(false);
        }
    };

    const manejarSeleccionArchivo = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validar extensión
            if (!file.name.endsWith('.csv')) {
                setError('Por favor seleccione un archivo CSV (.csv)');
                return;
            }
            
            // Validar tamaño (5MB máximo)
            const MAX_SIZE = 5 * 1024 * 1024; // 5MB
            if (file.size > MAX_SIZE) {
                setError(`El archivo excede el tamaño máximo de 5MB. Tamaño actual: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
                return;
            }
            
            setArchivo(file);
            setError('');
            setResultado(null);
        }
    };

    const subirArchivo = async () => {
        if (!archivo) {
            setError('Por favor seleccione un archivo');
            return;
        }

        // Confirmación antes de procesar
        const confirmar = window.confirm(
            '⚠️ ADVERTENCIA IMPORTANTE:\n\n' +
            '• Las preguntas que NO estén en el archivo serán DESACTIVADAS\n' +
            '• Las preguntas existentes serán ACTUALIZADAS con la nueva información\n' +
            '• Las nuevas preguntas serán CREADAS\n\n' +
            '¿Está seguro de que desea continuar con la carga masiva?'
        );
        
        if (!confirmar) {
            return;
        }

        try {
            setCargando(true);
            setError('');
            
            const formData = new FormData();
            formData.append('archivo', archivo);

            const response = await apiClient.post('/bulk/cargar-preguntas-masivo', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setResultado(response.data);
            setArchivo(null);
            // Limpiar input file
            const fileInput = document.getElementById('archivo-input-preguntas');
            if (fileInput) fileInput.value = '';
            
            // Recargar resumen
            await cargarResumenPreguntas();

        } catch (error) {
            setError('Error subiendo archivo: ' + (error.response?.data?.detail || error.message));
        } finally {
            setCargando(false);
        }
    };

    const ResultadoCarga = ({ resultado }) => {
        const { resultados } = resultado;
        const totalProcesados = resultados.preguntas_creadas + resultados.preguntas_actualizadas;

        return (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center mb-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <h3 className="text-lg font-semibold text-green-800">Carga Completada</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-blue-600">{resultados.preguntas_creadas}</div>
                        <div className="text-sm text-gray-600">Preguntas Creadas</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-green-600">{resultados.preguntas_actualizadas}</div>
                        <div className="text-sm text-gray-600">Preguntas Actualizadas</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-yellow-600">{resultados.preguntas_desactivadas}</div>
                        <div className="text-sm text-gray-600">Preguntas Desactivadas</div>
                    </div>
                </div>

                {resultados.errores && resultados.errores.length > 0 && (
                    <div className="mt-4">
                        <h4 className="font-semibold text-red-800 mb-2">Errores encontrados:</h4>
                        <ul className="list-disc list-inside text-sm text-red-700 bg-red-50 p-3 rounded max-h-40 overflow-y-auto">
                            {resultados.errores.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="mt-4 text-center">
                    <span className="text-lg font-semibold text-gray-700">
                        Total procesados: {totalProcesados} preguntas
                    </span>
                </div>
            </div>
        );
    };

    const ResumenPreguntas = ({ resumen }) => {
        if (!resumen) return null;

        return (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center mb-3">
                    <BarChart3 className="h-5 w-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-semibold text-blue-800">Resumen Actual de Preguntas</h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-green-600">{resumen.total_activas}</div>
                        <div className="text-sm text-gray-600">Preguntas Activas</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-gray-400">{resumen.total_inactivas}</div>
                        <div className="text-sm text-gray-600">Preguntas Inactivas</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-blue-600">{resumen.por_tipo.lider_empleado}</div>
                        <div className="text-sm text-gray-600">Líder → Empleado</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                        <div className="text-2xl font-bold text-purple-600">{resumen.por_tipo.empleado_lider}</div>
                        <div className="text-sm text-gray-600">Empleado → Líder</div>
                    </div>
                </div>

                {resumen.categorias.length > 0 && (
                    <div className="mt-4">
                        <h4 className="font-semibold text-gray-700 mb-2">Distribución por Categorías:</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {resumen.categorias.slice(0, 6).map((cat, index) => (
                                <div key={index} className="flex justify-between items-center p-2 bg-white rounded border text-sm">
                                    <span className="text-gray-700">{cat.nombre}</span>
                                    <span className="font-semibold text-blue-600">{cat.total}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="sm:flex sm:items-center mb-6">
                <div className="sm:flex-auto">
                    <h1 className="text-2xl font-semibold text-gray-900">Carga Masiva de Preguntas</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Carga y sincronización masiva de preguntas de evaluación desde archivo CSV
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">

                {/* Resumen Actual */}
                <ResumenPreguntas resumen={resumenPreguntas} />

                {/* Instrucciones */}
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                        <div>
                            <h3 className="font-semibold text-yellow-800 mb-2">Instrucciones Importantes:</h3>
                            <ul className="text-sm text-yellow-700 space-y-1">
                                <li>• <strong>Descarga la plantilla CSV</strong> y llénala con las preguntas</li>
                                <li>• <strong>Columnas requeridas:</strong> pregunta, categoria, tipo_evaluacion, peso, orden</li>
                                <li>• <strong>tipo_evaluacion:</strong> debe ser "lider_empleado" o "empleado_lider"</li>
                                <li>• <strong>Preguntas existentes</strong> (mismo texto) serán actualizadas</li>
                                <li>• <strong>Preguntas no incluidas</strong> en el archivo serán desactivadas</li>
                                <li>• <strong>Nuevas preguntas</strong> serán creadas automáticamente</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Botón Descargar Plantilla */}
                <div className="mb-6">
                    <button
                        onClick={descargarPlantilla}
                        disabled={cargando}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <Download className="h-5 w-5 mr-2" />
                        {cargando ? 'Generando...' : 'Descargar Plantilla CSV'}
                    </button>
                </div>

                {/* Subir Archivo */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seleccionar archivo CSV con preguntas:
                    </label>
                    <div className="flex items-center space-x-4">
                        <input
                            id="archivo-input-preguntas"
                            type="file"
                            accept=".csv"
                            onChange={manejarSeleccionArchivo}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <button
                            onClick={subirArchivo}
                            disabled={!archivo || cargando}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <Upload className="h-5 w-5 mr-2" />
                            {cargando ? 'Procesando...' : 'Subir Archivo'}
                        </button>
                    </div>
                    {archivo && (
                        <p className="mt-2 text-sm text-gray-600">
                            Archivo seleccionado: {archivo.name}
                        </p>
                    )}
                </div>

                {/* Error */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                            <span className="text-red-800">{error}</span>
                        </div>
                    </div>
                )}

                {/* Resultado */}
                {resultado && <ResultadoCarga resultado={resultado} />}

                {/* Loading */}
                {cargando && (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">Procesando...</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const CargaMasivaPreguntas = () => {
    return (
        <DashboardLayout>
            <CargaMasivaPreguntasContent />
        </DashboardLayout>
    );
};

export default CargaMasivaPreguntas;
